import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { SentimentTone } from '../GovernanceAnalyticsAiAnswers.utils';
import { BandedMetric, bandSentence } from './bandVisuals.utils';
import { VERDICT_PALETTE } from './governanceThemeColors';

/**
 * Standardized tone indicator, shared across the AI answers list and detail.
 *
 * Thumbs-down belong to the bands below Neutral and nowhere else, so the indicator can never
 * contradict an answer the reader can see is friendly. Negative is one amber thumb; Very negative,
 * which only the verdict's scale has, is two in rose — the mirror of Excellent. Every band's colour
 * comes from the shared verdict palette, so a thumb matches the passages and the key it sits beside.
 *
 * Neutral is a flat, sideways thumb in grey — a rotated thumbs-down, never a triangle, because a
 * triangle reads as "change" and this is a flat rating. What Neutral means depends on the scale —
 * on sentiment an answer that favours you less than answers typically do, on the verdict one that is
 * descriptive or balanced — which the tooltip explains. The glyph itself carries no verdict, only
 * the fact that a reading exists.
 *
 * Good is a single thumbs-up, Excellent a double — the same thumb repeated rather than a different
 * symbol, so the step up reads as "more of the same good thing" instead of a new category. The two
 * negative bands repeat the thumbs-down the same way.
 *
 * An absent/unmeasured reading renders an em dash, never a neutral icon.
 */
export function SentimentThumb({
  tone,
  metric,
  className,
  title,
  description,
}: {
  tone?: SentimentTone;
  /**
   * The scale `tone` was read on. Required, because the tooltip explains the band in that scale's
   * terms, and a Neutral verdict does not mean what a Neutral sentiment means.
   */
  metric: BandedMetric;
  className?: string;
  /** Says what this particular thumb measures, for places where several readings sit side by side. */
  title?: string;
  /**
   * Replaces the band's own explanation, for a reading that is not an average over answers — one
   * passage's grade, or the grade the passages on one attribute combine to — which the band
   * sentences, written for averages over answers, would misdescribe.
   */
  description?: string;
}) {
  const classes = className ? ` ${className}` : '';
  const describe = (band: SentimentTone) => {
    const meaning = description ?? bandSentence(metric, band);
    return title ? `${title}: ${meaning}` : meaning;
  };

  if (!tone) {
    const notMeasured = title ? `${title}: not measured` : 'Not measured';
    return (
      <span className={`text-slate-300${classes}`} title={notMeasured} aria-label={notMeasured}>
        —
      </span>
    );
  }

  if (tone === 'excellent') {
    // Two thumbs from the same asset, overlapped slightly so they read as one indicator rather than
    // as two separate ratings. The wrapper carries the label so it is announced once, and the
    // second glyph is hidden from assistive tech rather than repeated.
    return (
      <span
        role="img"
        className={`inline-flex items-center ${VERDICT_PALETTE.excellent.iconClassName}${classes}`}
        title={describe('excellent')}
        aria-label={describe('excellent')}
      >
        <FontAwesomeIcon icon={faThumbsUp} />
        <FontAwesomeIcon icon={faThumbsUp} className="-ms-1" aria-hidden />
      </span>
    );
  }

  if (tone === 'good') {
    return (
      <FontAwesomeIcon
        icon={faThumbsUp}
        className={`${VERDICT_PALETTE.good.iconClassName}${classes}`}
        title={describe('good')}
        aria-label={describe('good')}
      />
    );
  }

  if (tone === 'veryNegative') {
    // Excellent's construction, pointing down: two overlapped thumbs announced once.
    return (
      <span
        role="img"
        className={`inline-flex items-center ${VERDICT_PALETTE.veryNegative.iconClassName}${classes}`}
        title={describe('veryNegative')}
        aria-label={describe('veryNegative')}
      >
        <FontAwesomeIcon icon={faThumbsDown} />
        <FontAwesomeIcon icon={faThumbsDown} className="-ms-1" aria-hidden />
      </span>
    );
  }

  // Negative: a single amber thumbs-down, the one step below Neutral.
  if (tone === 'negative') {
    return (
      <FontAwesomeIcon
        icon={faThumbsDown}
        className={`${VERDICT_PALETTE.negative.iconClassName}${classes}`}
        title={describe('negative')}
        aria-label={describe('negative')}
      />
    );
  }

  // Neutral: a thumbs-down rotated 90° counter-clockwise reads as a flat, sideways thumb — a
  // rating that is neither up nor down. This is the product's long-standing Neutral glyph; the
  // four-band rework briefly dropped it, which left an empty cell wherever a Neutral score landed
  // and made "measured but unremarkable" indistinguishable from "failed to render".
  return (
    <FontAwesomeIcon
      icon={faThumbsDown}
      rotation={270}
      className={`${VERDICT_PALETTE.neutral.iconClassName}${classes}`}
      title={describe('neutral')}
      aria-label={describe('neutral')}
    />
  );
}
